import type { ToolApprovalBatch, ToolApprovalRenderer } from "#components/ui/tool-approval/renderers/ToolApprovalRenderer.types.ts";
import { createCalendarEventRenderer } from "#features/conversations/components/prepared-action/CalendarEventCard.tsx";
import { replyToFeedbackRenderer } from "#features/conversations/components/prepared-action/FeedbackReplyCard.tsx";
import { sendGoodVibeRenderer } from "#features/conversations/components/prepared-action/GoodVibeCard.tsx";
import {
    createSkillResourceRenderer,
    deleteSkillResourceRenderer,
    updateSkillResourceRenderer
} from "#features/conversations/components/prepared-action/SkillResourceCard.tsx";
import { sendSlackMessageRenderer } from "#features/conversations/components/prepared-action/SlackMessageCard.tsx";

/** One entry per tool with a bespoke card — add a card file and one line here. Absent ⇒ generic content. */
export const TOOL_APPROVAL_RENDERERS: Partial<Record<string, ToolApprovalRenderer>> = {
    send_good_vibe: sendGoodVibeRenderer,
    reply_to_feedback: replyToFeedbackRenderer,
    send_slack_message: sendSlackMessageRenderer,
    create_skill_resource: createSkillResourceRenderer,
    update_skill_resource: updateSkillResourceRenderer,
    delete_skill_resource: deleteSkillResourceRenderer,
    create_calendar_event: createCalendarEventRenderer
};

/** The renderer that will draw this call, or null when the tool has none or its args fail the card's guard. */
export function resolveToolApprovalRenderer(call: { toolName: string; args: Record<string, unknown> }): ToolApprovalRenderer | null {
    const renderer = Object.hasOwn(TOOL_APPROVAL_RENDERERS, call.toolName) ? TOOL_APPROVAL_RENDERERS[call.toolName] : undefined;
    if (renderer === undefined) {
        return null;
    }

    try {
        return renderer.matches(call.args) ? renderer : null;
    } catch {
        return null;
    }
}

/**
 * The list a batch reads as: two or more calls that all draw through one renderer that sets `batch`. A mixed batch, or one
 * whose renderer sets none, keeps the stacked layout.
 */
export function resolveBatchList(calls: { toolName: string; args: Record<string, unknown> }[]): ToolApprovalBatch | null {
    const renderer = calls.length > 1 ? resolveToolApprovalRenderer(calls[0]!) : null;

    return renderer?.batch !== undefined && calls.every(call => resolveToolApprovalRenderer(call) === renderer) ? renderer.batch : null;
}
